import api from "src/core/axios";
import type {
  CalendarConnection,
  BookingEventType,
  BookingAvailabilityRule,
  BookingDateOverride,
  Booking,
} from "src/types/calendar.types";

const BASE = "/integrations/google-calendar";
const BOOKING = "/booking";

// Backend wraps every response in `{ data: <payload>, ... }` via APIResponseInterceptor.
// `axios.get()` returns `{ data: <wrapper>, ... }`. The actual payload is at `.data.data`.
function unwrap<T>(res: { data: { data?: T } | T }): T {
  const body: any = (res as any).data;
  return (body && typeof body === "object" && "data" in body ? body.data : body) as T;
}

export const CalendarService = {
  async getAuthUrl(
    params: { reconnectId?: string } = {},
  ): Promise<{ url: string }> {
    return unwrap<{ url: string }>(await api.get(`${BASE}/auth-url`, { params }));
  },
  async listConnections(): Promise<{ data: CalendarConnection[] }> {
    const data = unwrap<CalendarConnection[]>(
      await api.get(`${BASE}/connections`),
    );
    return { data };
  },
  async disconnect(id: string): Promise<void> {
    await api.delete(`${BASE}/connections/${id}`);
  },
  async refreshCalendars(id: string): Promise<{ data: CalendarConnection }> {
    const data = unwrap<CalendarConnection>(
      await api.post(`${BASE}/connections/${id}/refresh-calendars`),
    );
    return { data };
  },

  async listEventTypes(): Promise<BookingEventType[]> {
    return unwrap<BookingEventType[]>(await api.get(`${BOOKING}/event-types`));
  },
  async getEventType(id: string): Promise<{
    eventType: BookingEventType;
    rules: BookingAvailabilityRule[];
    overrides: BookingDateOverride[];
  }> {
    // Backend returns the BookingEventType row with `availabilityRules` and
    // `dateOverrides` included via Prisma's `include`. Reshape into the
    // {eventType, rules, overrides} contract the page expects.
    const row = unwrap<
      BookingEventType & {
        availabilityRules?: BookingAvailabilityRule[];
        dateOverrides?: BookingDateOverride[];
      }
    >(await api.get(`${BOOKING}/event-types/${id}`));
    const { availabilityRules, dateOverrides, ...eventType } = row;
    return {
      eventType: eventType as BookingEventType,
      rules: availabilityRules ?? [],
      // <input type="date"> needs YYYY-MM-DD; trim ISO timestamps from Prisma.
      overrides: (dateOverrides ?? []).map((o) => ({
        ...o,
        date: typeof o.date === "string" ? o.date.slice(0, 10) : o.date,
      })),
    };
  },
  async createEventType(
    body: Partial<BookingEventType>,
  ): Promise<BookingEventType> {
    return unwrap<BookingEventType>(
      await api.post(`${BOOKING}/event-types`, body),
    );
  },
  async updateEventType(
    id: string,
    body: Partial<BookingEventType>,
  ): Promise<BookingEventType> {
    return unwrap<BookingEventType>(
      await api.patch(`${BOOKING}/event-types/${id}`, body),
    );
  },
  async deleteEventType(id: string): Promise<void> {
    await api.delete(`${BOOKING}/event-types/${id}`);
  },
  async putAvailability(
    id: string,
    body: { rules: any[]; overrides: any[] },
  ) {
    return unwrap(
      await api.put(`${BOOKING}/event-types/${id}/availability`, body),
    );
  },

  // AI-tool callable surface — also useful for the admin "test booking" button.
  async getSlots(
    eventTypeId: string,
    params: { from: string; to: string; timezone?: string },
  ): Promise<{ slots: { start: string; end: string }[]; timezone: string }> {
    return unwrap(
      await api.get(`${BOOKING}/event-types/${eventTypeId}/slots`, { params }),
    );
  },
  async bookSlot(
    eventTypeId: string,
    body: {
      startsAt: string;
      attendee: { name: string; email: string; phone?: string };
      notes?: string;
      source?: "ai_agent" | "manual" | "api";
      correlationId?: string;
    },
  ): Promise<{
    appointment: { id: string; startsAt: string; endsAt: string };
    googleEventId: string;
  }> {
    return unwrap(
      await api.post(`${BOOKING}/event-types/${eventTypeId}/book`, body),
    );
  },

  async listBookings(params?: {
    from?: string;
    to?: string;
    eventTypeId?: string;
    status?: string;
    limit?: number;
    cursor?: string;
  }): Promise<{ data: Booking[]; nextCursor: string | null }> {
    const payload = unwrap<{ data: Booking[]; nextCursor: string | null }>(
      await api.get(`${BOOKING}/bookings`, { params }),
    );
    // Defensive: tolerate either the paginated shape or a bare array
    if (Array.isArray(payload)) {
      return { data: payload as unknown as Booking[], nextCursor: null };
    }
    return {
      data: payload?.data ?? [],
      nextCursor: payload?.nextCursor ?? null,
    };
  },
  async cancelBooking(id: string): Promise<void> {
    await api.delete(`${BOOKING}/bookings/${id}`);
  },
};
